import React, { useState } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { SectionHeading } from "components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { ReactComponent as SvgDecoratorBlob1 } from "images/svg-decorator-blob-5.svg";
import { ReactComponent as SvgDecoratorBlob2 } from "images/svg-decorator-blob-7.svg";

import {
  designsImages,
  developmentsImages,
  promotionsImages
} from "data/workImages";

/* ---------------- STYLES ---------------- */

const HeaderRow = tw.div`flex justify-between items-center flex-col xl:flex-row`;
const Header = tw(SectionHeading)``;
const TabsControl = tw.div`flex flex-wrap bg-gray-200 px-2 py-2 rounded leading-none mt-12 xl:mt-0`;

const TabControl = styled.div`
  ${tw`cursor-pointer px-6 py-3 mt-2 sm:mt-0 sm:mr-2 last:mr-0 text-gray-600 font-medium rounded-sm transition duration-300 text-sm sm:text-base w-1/2 sm:w-auto text-center`}
  &:hover {
    ${tw`bg-gray-300 text-gray-700`}
  }
  ${props => props.active && tw`bg-primary-500! text-gray-100!`}
`;

const TabContent = tw(motion.div)`mt-6 flex flex-wrap sm:-mr-10 md:-mr-6 lg:-mr-12`;
const CardContainer = tw.div`mt-10 w-full sm:w-1/2 md:w-1/3 lg:w-1/4 sm:pr-10 md:pr-6 lg:pr-12`;
const Card = tw(motion.div)`bg-gray-200 rounded-b block max-w-xs mx-auto sm:max-w-none sm:mx-0`;

const CardImageContainer = styled.div`
  ${props => css`background-image: url("${props.imageSrc}");`}
  ${tw`h-56 xl:h-64 bg-center bg-cover relative rounded-t`}
`;

const CardHoverOverlay = styled(motion.div)`
  background-color: rgba(255,255,255,0.5);
  ${tw`absolute inset-0 flex justify-center items-center`}
`;

const CardButton = tw(PrimaryButtonBase)`text-sm`;

const DecoratorBlob1 = styled(SvgDecoratorBlob1)`
  ${tw`pointer-events-none -z-20 absolute right-0 top-0 h-64 w-64 opacity-15 transform translate-x-2/3 -translate-y-12 text-pink-400`}
`;
const DecoratorBlob2 = styled(SvgDecoratorBlob2)`
  ${tw`pointer-events-none -z-20 absolute left-0 bottom-0 h-80 w-80 opacity-15 transform -translate-x-2/3 text-primary-500`}
`;

/* ---------------- COMPONENT ---------------- */

export default function PortfolioTabs({ heading = "Checkout our work" }) {

  const tabs = {
    All: [...new Set([
      ...designsImages,
      ...developmentsImages,
      ...promotionsImages
    ])].map(img => ({ imageSrc: img })),

    Designs: designsImages.map(img => ({ imageSrc: img })),
    Developments: developmentsImages.map(img => ({ imageSrc: img })),
    Promotions: promotionsImages.map(img => ({ imageSrc: img }))
  };

  const tabsKeys = Object.keys(tabs);
  const [activeTab, setActiveTab] = useState(tabsKeys[0]);
  const [lightboxImage, setLightboxImage] = useState(null);

  return (
    <Container>
      <ContentWithPaddingXl>

        <HeaderRow>
          <Header>{heading}</Header>

          <TabsControl>
            {tabsKeys.map(tab => (
              <TabControl
                key={tab}
                active={activeTab === tab}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </TabControl>
            ))}
          </TabsControl>
        </HeaderRow>

        {tabsKeys.map(tabKey => (
          <TabContent
            key={tabKey}
            variants={{
              current: { opacity: 1, scale: 1, display: "flex" },
              hidden: { opacity: 0, scale: 0.8, display: "none" }
            }}
            transition={{ duration: 0.4 }}
            initial={activeTab === tabKey ? "current" : "hidden"}
            animate={activeTab === tabKey ? "current" : "hidden"}
          >

            {tabs[tabKey].map((card, index) => (
              <CardContainer key={index}>
                <Card initial="rest" whileHover="hover" animate="rest">

                  <CardImageContainer
                    imageSrc={card.imageSrc}
                    onClick={() => setLightboxImage(card.imageSrc)}
                    style={{ cursor: "pointer" }}
                  >

                    <CardHoverOverlay
                      variants={{
                        hover: { opacity: 1, height: "auto" },
                        rest: { opacity: 0, height: 0 }
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      <CardButton>View</CardButton>
                    </CardHoverOverlay>

                  </CardImageContainer>

                </Card>
              </CardContainer>
            ))}

          </TabContent>
        ))}

      </ContentWithPaddingXl>

      <DecoratorBlob1 />
      <DecoratorBlob2 />

      {/* ---------- LIGHTBOX ---------- */}
      {lightboxImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setLightboxImage(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}
        >
          <motion.img
            src={lightboxImage}
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3 }}
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: "8px"
            }}
            onClick={(e) => e.stopPropagation()}
          />

          <button
            onClick={() => setLightboxImage(null)}
            style={{
              position: "absolute",
              top: 20,
              right: 30,
              fontSize: "32px",
              background: "none",
              color: "#fff",
              border: "none",
              cursor: "pointer"
            }}
          >
            ×
          </button>
        </motion.div>
      )}

    </Container>
  );
}
